import "../css/header.scss";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData, { siteConfig } from "../Datas/productData";

function Header() {
  const location = useLocation();
  const { cartItems, wishlist = [] } = useContext(CartContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);

  const cartCount = cartItems.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );

  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return [];
    return productData
      .filter((product) =>
        [product.name, product.category, ...(product.tags || [])]
          .join(" ")
          .toLowerCase()
          .includes(normalized)
      )
      .slice(0, 5);
  }, [query]);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setQuery("");

    if (location.hash) {
      window.requestAnimationFrame(() => {
        document.querySelector(location.hash)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.classList.toggle("menu-open", mobileOpen || searchOpen);
    return () => document.body.classList.remove("menu-open");
  }, [mobileOpen, searchOpen]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  return (
    <>
      <div className="announcement-bar" role="status">
        <span>{siteConfig.announcement}</span>
      </div>

      <header className="site-header">
        <div className="site-header__inner">
          <button
            className="site-header__mobile-toggle"
            type="button"
            aria-label="Open navigation"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
          >
            <FiMenu aria-hidden="true" />
          </button>

          <nav className="site-header__nav site-header__nav--primary" aria-label="Primary navigation">
            <NavLink to="/">Home</NavLink>
            <NavLink to="/shop">Shop</NavLink>
            <NavLink to="/about">Atelier</NavLink>
            <Link to="/#journal">Journal</Link>
          </nav>

          <Link className="site-header__brand" to="/" aria-label={`${siteConfig.brandName} home`}>
            <span>{siteConfig.brandName}</span>
            <small>{siteConfig.tagline}</small>
          </Link>

          <div className="site-header__tools">
            <span className="site-header__locale" aria-label="Selected locale">
              EUR · EN
            </span>
            <button type="button" aria-label="Search" onClick={() => setSearchOpen(true)}>
              <FiSearch aria-hidden="true" />
            </button>
            <Link to="/contact" aria-label="Customer service">
              <FiUser aria-hidden="true" />
            </Link>
            <Link
              className="site-header__count-link"
              to="/shop"
              aria-label={`Wishlist with ${wishlist.length} items`}
            >
              <FiHeart aria-hidden="true" />
              {wishlist.length > 0 && <span>{wishlist.length}</span>}
            </Link>
            <Link
              className="site-header__count-link"
              to="/cartpage"
              aria-label={`Shopping bag with ${cartCount} items`}
            >
              <FiShoppingBag aria-hidden="true" />
              {cartCount > 0 && <span>{cartCount}</span>}
            </Link>
          </div>
        </div>
      </header>

      <div className={`site-drawer ${mobileOpen ? "is-open" : ""}`} aria-hidden={!mobileOpen}>
        <button
          className="site-drawer__backdrop"
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
        <aside className="site-drawer__panel">
          <div className="site-drawer__head">
            <span>Menu</span>
            <button type="button" aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
              <FiX aria-hidden="true" />
            </button>
          </div>
          <nav aria-label="Mobile navigation">
            <NavLink to="/">Home</NavLink>
            <NavLink to="/shop">Shop</NavLink>
            <NavLink to="/about">Atelier</NavLink>
            <Link to="/#journal">Journal</Link>
            <NavLink to="/contact">Contact</NavLink>
          </nav>
          <div className="site-drawer__meta">
            <span>Country/region</span>
            <strong>Germany · EUR €</strong>
            <span>Language</span>
            <strong>English</strong>
          </div>
        </aside>
      </div>

      <div className={`search-overlay ${searchOpen ? "is-open" : ""}`} aria-hidden={!searchOpen}>
        <button className="search-overlay__backdrop" type="button" aria-label="Close search" onClick={closeSearch} />
        <section className="search-overlay__panel" role="search">
          <div className="search-overlay__top">
            <label htmlFor="site-search">Search the collection</label>
            <button type="button" aria-label="Close search" onClick={closeSearch}>
              <FiX aria-hidden="true" />
            </button>
          </div>
          <div className="search-overlay__field">
            <FiSearch aria-hidden="true" />
            <input
              id="site-search"
              ref={searchInputRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, colour or style"
              autoComplete="off"
            />
          </div>
          {query && (
            <div className="search-overlay__results" aria-live="polite">
              {results.length > 0 ? (
                results.map((product) => (
                  <Link key={product.id} to={`/shopDetails/${product.id}`} onClick={closeSearch}>
                    <img src={product.image} alt="" />
                    <span>
                      <strong>{product.name}</strong>
                      <small>{product.category}</small>
                    </span>
                    <em>€{Number(product.price).toFixed(2)}</em>
                  </Link>
                ))
              ) : (
                <p>No pieces found for “{query}”.</p>
              )}
            </div>
          )}
          <Link className="search-overlay__all" to="/shop" onClick={closeSearch}>
            View the complete collection
          </Link>
        </section>
      </div>
    </>
  );
}

export default Header;
